import { mat3, mat4, ReadonlyMat4, vec3 } from 'gl-matrix';
import { AnnotationPlane } from '~/domain/annotation/annotation';
import { PointMm, toPointMm } from '~/domain/geometry/patientGeometry';
import type { Grid } from '~/engine/data/common/Grid';
import type { Environment } from '~/engine/data/Environment';
import type { ImageObject } from '~/engine/data/image/ImageObject';
import {
    displayedImage,
    FusedImageDisplay,
} from '~/engine/view/layers/fusedImageDisplay';

/**
 * Which image a pane shows, and how a point on the pane maps into that
 * image's own frame.
 *
 * A pane is a view of one Environment. It draws the Environment's primary
 * image (alone or blended with the fused secondary), the registered
 * secondary alone, or nothing. Points on the pane are in the primary's
 * patient frame. A secondary drawn alone is the same volume moved by the
 * registration, so a point on the pane lands on a different patient
 * position of the secondary's own frame.
 *
 * The answer is read from live state on every call and never cached: a
 * registration drag, an undo or a new fusion changes it between two calls.
 * A caller that spans two events (a two-click placement) keeps the first
 * answer and checks it with {@link samePaneImage} before it commits.
 */

/** The pane draws no image: a secondary-layer pane with nothing fused. */
interface NoPaneImage {
    readonly kind: 'none';
}

/** The pane draws `image`, the primary (alone or blended) or the secondary. */
export interface ShownPaneImage {
    readonly kind: 'primary' | 'secondary';
    /**
     * The image the pane draws. Its grid is in the pane's frame, so a pixel
     * read at a pane point needs no mapping.
     */
    readonly image: ImageObject;
    /**
     * `image.grid` at the time of the call. Its transformation is where the
     * registration placed the image on the pane when the answer was read.
     */
    readonly grid: Grid;
    /** The series of the shown image: the key of a record made on the pane. */
    readonly seriesUid: string;
    /** A pane point (primary frame, mm) in the shown image's own frame. */
    toImageFrame(point: PointMm): PointMm;
    /** A point in the shown image's own frame (mm) on the pane. */
    fromImageFrame(point: PointMm): PointMm;
    /** A plane of the pane in the shown image's own frame. */
    planeToImageFrame(plane: AnnotationPlane): AnnotationPlane;
    /** A plane in the shown image's own frame on the pane. */
    planeFromImageFrame(plane: AnnotationPlane): AnnotationPlane;
}

export type PaneImage = NoPaneImage | ShownPaneImage;

/** What {@link paneImageOf} reads of a view. */
export interface PaneImageSource {
    readonly fusedImageDisplay: FusedImageDisplay;
    readonly environment: Pick<Environment, 'image' | 'secondaryImage'>;
}

const NO_PANE_IMAGE: NoPaneImage = { kind: 'none' };

/**
 * The image `view` shows. A blended view shows the primary: its points and
 * its records belong to the primary, and the secondary is only drawn over
 * it. A view whose Environment has no primary image shows nothing.
 */
export function paneImageOf(view: PaneImageSource): PaneImage {
    const { environment } = view;
    if (!environment.image) {
        return NO_PANE_IMAGE;
    }
    const image = displayedImage(view.fusedImageDisplay, environment);
    if (!image) {
        return NO_PANE_IMAGE;
    }
    const kind = image === environment.image ? 'primary' : 'secondary';
    const grid = image.grid;
    const ownGrid = image.parent.grid;
    return {
        kind,
        image,
        grid,
        seriesUid: image.parent.seriesUid,
        ...(grid === ownGrid
            ? IDENTITY_MAPPING
            : rigidMapping(
                  mat4.multiply(
                      mat4.create(),
                      ownGrid.voxelToWorld,
                      grid.worldToVoxel
                  )
              )),
    };
}

/**
 * Whether two answers describe the same pane image through the same
 * registration: the same side, the same series, the same image object and
 * a grid transformation equal element by element. The grid object itself is
 * not compared: re-applying an unchanged registration builds a new grid in
 * the same place, and the pane has not moved.
 */
export function samePaneImage(a: PaneImage, b: PaneImage): boolean {
    if (a.kind === 'none' || b.kind === 'none') {
        return a.kind === b.kind;
    }
    return (
        a.kind === b.kind &&
        a.seriesUid === b.seriesUid &&
        a.image.parent === b.image.parent &&
        mat4.exactEquals(a.grid.getTransformation(), b.grid.getTransformation())
    );
}

type FrameMapping = Pick<
    ShownPaneImage,
    | 'toImageFrame'
    | 'fromImageFrame'
    | 'planeToImageFrame'
    | 'planeFromImageFrame'
>;

const IDENTITY_MAPPING: FrameMapping = {
    toImageFrame: (point) => point,
    fromImageFrame: (point) => point,
    planeToImageFrame: (plane) => plane,
    planeFromImageFrame: (plane) => plane,
};

/**
 * The mapping through the rigid transform `paneToImage` (pane frame to the
 * image's own frame) and its inverse. A plane keeps its name and series;
 * its normal turns with the transform and its offset follows the moved
 * points: for `p' = R p + t`, `dot(p', R n) = dot(p, n) + dot(t, R n)`.
 */
function rigidMapping(paneToImage: ReadonlyMat4): FrameMapping {
    const imageToPane = mat4.invert(mat4.create(), paneToImage);
    const mapPoint =
        (matrix: ReadonlyMat4) =>
        (point: PointMm): PointMm =>
            toPointMm(
                vec3.transformMat4(
                    vec3.create(),
                    vec3.fromValues(point[0], point[1], point[2]),
                    matrix
                )
            );
    const mapPlane =
        (matrix: ReadonlyMat4) =>
        (plane: AnnotationPlane): AnnotationPlane => {
            const rotation = mat3.fromMat4(mat3.create(), matrix);
            const viewDirection = vec3.normalize(
                vec3.create(),
                vec3.transformMat3(vec3.create(), plane.viewDirection, rotation)
            );
            const translation = mat4.getTranslation(vec3.create(), matrix);
            return {
                ...plane,
                viewDirection,
                planeCoordinateMm:
                    plane.planeCoordinateMm +
                    vec3.dot(translation, viewDirection),
            };
        };
    return {
        toImageFrame: mapPoint(paneToImage),
        fromImageFrame: mapPoint(imageToPane),
        planeToImageFrame: mapPlane(paneToImage),
        planeFromImageFrame: mapPlane(imageToPane),
    };
}
